import React, { useRef, useState } from 'react';

interface SelectedImage {
  dataUrl: string;
  type: string;
  name: string;
}

interface ChatInputProps {
  disabled: boolean;
  onSend: (text: string, image?: { dataUrl: string; type: string }) => void;
}

// FileReader.readAsDataURL yields "data:<mime>;base64,…" — exactly the inlined
// data URL the socket expects as file_data.
function readImageAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const ChatInput: React.FC<ChatInputProps> = ({ disabled, onSend }) => {
  const [text, setText] = useState('');
  const [image, setImage] = useState<SelectedImage | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSend = !disabled && (text.trim() !== '' || image !== null);

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim(), image ? { dataUrl: image.dataUrl, type: image.type } : undefined);
    setText('');
    setImage(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const dataUrl = await readImageAsDataUrl(file);
      setImage({ dataUrl, type: file.type, name: file.name });
    } catch (err) {
      console.warn('ChatInput: failed to read image', err);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        borderTop: '1px solid #e5e7eb',
        background: '#fff',
      }}
    >
      {image && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <img
              src={image.dataUrl}
              alt={image.name}
              style={{
                width: 56,
                height: 56,
                objectFit: 'cover',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
              }}
            />
            <button
              onClick={() => setImage(null)}
              aria-label="Quitar imagen"
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: 'none',
                background: '#1f2937',
                color: '#fff',
                fontSize: 12,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Adjuntar imagen"
          title="Adjuntar imagen"
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            width: 38,
            height: 38,
            flexShrink: 0,
            background: '#fff',
            color: disabled ? '#9ca3af' : '#38b2ac',
            fontSize: 18,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          📎
        </button>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="Escribe al asistente…"
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 120,
            minHeight: 38,
            padding: '9px 12px',
            borderRadius: 10,
            border: '1px solid #e5e7eb',
            fontFamily: 'inherit',
            fontSize: 14,
            lineHeight: 1.4,
            outline: 'none',
            color: disabled ? '#9ca3af' : '#1f2937',
            background: disabled ? '#f3f4f6' : '#fff',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <button
          onClick={submit}
          disabled={!canSend}
          style={{
            border: 'none',
            borderRadius: 10,
            padding: '9px 16px',
            height: 38,
            background: canSend ? '#38b2ac' : '#9fd8d4',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: canSend ? 'pointer' : 'default',
          }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
